import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Tooltip,
    Pagination,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Backdrop,
    CircularProgress,
    Typography,
    Card,
    CardContent,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
    FilterList,
    RefreshSharp,
    Visibility,
    ErrorOutline,
    Inventory2Outlined,
    ScheduleOutlined,
    SendOutlined,
    LocalShippingOutlined,
    MarkEmailReadOutlined,
    ReportProblemOutlined,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { alpha } from "@mui/material/styles";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { blue } from "@mui/material/colors";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import FailedMessageSummaryCard from "./MessageQueue/FailedMessageSummaryCard";

const STATUS_OPTIONS = ["PENDING", "SENT",  "DELIVERED", "READ", "FAILED", "AUTO_RETRYING"];
const DATE_FIELD_OPTIONS = [
    { value: "created_at", label: "Created At" },
];

const getStatusColor = (status) => {
    switch (status) {
        case "PENDING":
            return "warning";
        case "SENT":
            return "info";
        case "DELIVERED":
            return "success";
        case "READ":
            return "primary";
        case "FAILED":
            return "error";
        case "AUTO_RETRYING":
            return "warning";
        default:
            return "default";
    }
};

const getStatusLabel = (status) => {
    if (status === "AUTO_RETRYING") return "AUTO RETRYING";
    return status;
};

// Helper function to check if error code is 131026
const hasErrorCode131026 = (row) => {
    if (row.error_code === 131026) {
        return true;
    }
    
    // Fallback: check error field
    if (row.error) {
        try {
            // If error is a string, try to parse it
            let errorData = row.error;
            if (typeof errorData === 'string') {
                errorData = JSON.parse(errorData);
            }
            
            // If it's an array, check each error object
            if (Array.isArray(errorData)) {
                return errorData.some(err => err.code === 131026);
            }
            
            // If it's an object, check directly
            if (errorData && errorData.code === 131026) {
                return true;
            }
        } catch (e) {
            // If parsing fails, check if error string contains the code
            if (typeof row.error === 'string' && row.error.includes('131026')) {
                return true;
            }
        }
    }
    
    return false;
};

// Helper function to extract error message
const getErrorMessage = (row) => {
    // First check error_code and error_message fields
    if (row.error_code === 131026 && row.error_message) {
        return row.error_message;
    }
    
    // Fallback: check error field
    if (row.error) {
        try {
            let errorData = row.error;
            if (typeof errorData === 'string') {
                errorData = JSON.parse(errorData);
            }
            
            if (Array.isArray(errorData)) {
                const error131026 = errorData.find(err => err.code === 131026);
                if (error131026) {
                    return error131026.message || error131026.title || error131026.details || "Message undeliverable";
                }
                // If no 131026 error, return first error message
                if (errorData.length > 0) {
                    return errorData[0].message || errorData[0].title || errorData[0].details || "Error occurred";
                }
            }
            
            if (errorData && errorData.code === 131026) {
                return errorData.message || errorData.title || errorData.details || "Message undeliverable";
            }
            
            if (errorData && errorData.message) {
                return errorData.message;
            }
        } catch (e) {
            // If parsing fails, return the error string
            if (typeof row.error === 'string') {
                return row.error;
            }
        }
    }
    
    return null;
};

const countQueueStatistics = (rows) => {
    const stats = {
        total: rows.length,
        pending: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        auto_retrying: 0,
        failed_summary: [],
    };
    const failedGroups = new Map();

    rows.forEach((row) => {
        const status = String(row.status || "").toUpperCase();
        if (status === "PENDING") stats.pending += 1;
        else if (status === "SENT") stats.sent += 1;
        else if (status === "DELIVERED") stats.delivered += 1;
        else if (status === "READ") stats.read += 1;
        else if (status === "FAILED") stats.failed += 1;
        else if (status === "AUTO_RETRYING") stats.auto_retrying += 1;

        if (status !== "FAILED") return;
        const reason = getErrorMessage(row) || "Failed";
        const code = row.error_code ?? null;
        const key = `${code ?? "none"}|${reason}`;
        const group = failedGroups.get(key) || { error_code: code, reason, count: 0 };
        group.count += 1;
        failedGroups.set(key, group);
    });

    stats.failed_summary = Array.from(failedGroups.values());
    return stats;
};

// Get error tooltip text for 131026
const getErrorTooltipText = () => {
    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Error 131026 - Message Undeliverable
            </Typography>
            <Typography variant="body2" component="div">
                Possible reasons:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                <li>Invalid Number: The phone number is not registered with WhatsApp.</li>
                <li>Outdated App: Recipient is using an old version of WhatsApp.</li>
                <li>Terms of Service: Recipient has not accepted the latest Meta privacy policy.</li>
            </Typography>
        </Box>
    );
};

const MessageQueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
    });
    const [statistics, setStatistics] = useState({
        total: 0,
        pending: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
        auto_retrying: 0,
        failed_summary: [],
    });

    // Filter states
    const [filters, setFilters] = useState({
        status: "",
        recipient: "",
        campaign_name: "",
        template_name: "",
        template_id: "",
        date_from: null,
        date_to: null,
        date_field: "created_at",
        message_priority: "",
        language: "",
        per_page: 50,
        page: 1,
        sort_by: "created_at",
        sort_order: "desc",
    });

    const [selectedMessage, setSelectedMessage] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const notify = useNotification();
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const loadGeneration = useRef(0);

    useEffect(() => {
        fetchMessages();
    }, []);

    const toQueueParams = (source, page) => {
        const params = { ...source, page };

        Object.keys(params).forEach((key) => {
            if (params[key] === "" || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        if (params.date_from instanceof Date) {
            params.date_from = format(params.date_from, "yyyy-MM-dd");
        }
        if (params.date_to instanceof Date) {
            params.date_to = format(params.date_to, "yyyy-MM-dd");
        }

        return params;
    };

    const applyStatistics = (stats) => {
        if (!stats) return;
        setStatistics({
            ...stats,
            failed_summary: stats.failed_summary || [],
        });
    };

    const loadRowsForStatistics = async (sourceFilters, pageRows, pageInfo, generation) => {
        const total = Number(pageInfo?.total);
        const lastPage = Number(pageInfo?.last_page) || 1;
        const currentPage = Number(pageInfo?.current_page) || 1;
        if (!Number.isFinite(total) || pageRows.length >= total || lastPage <= 1) {
            return pageRows;
        }

        const rows = [];
        for (let page = 1; page <= lastPage; page += 1) {
            if (generation !== loadGeneration.current) return pageRows;
            if (page === currentPage) {
                rows.push(...pageRows);
                continue;
            }
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/message-queue`,
                { params: toQueueParams(sourceFilters, page) }
            );
            if (generation !== loadGeneration.current) return pageRows;
            rows.push(...(response.data?.data || []));
        }
        return rows;
    };

    const fetchMessages = async (page = 1, sourceFilters = filters) => {
        const generation = ++loadGeneration.current;
        setLoading(true);
        try {
            const params = toQueueParams(sourceFilters, page);

            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/message-queue`,
                { params }
            );

            if (generation !== loadGeneration.current) return;

            if (response.data?.success) {
                const pageRows = response.data.data || [];
                const pageInfo = response.data.pagination || pagination;
                setData(pageRows);
                setPagination(pageInfo);

                const apiStats = response.data.statistics;
                const filteredTotal = Number(pageInfo.total);
                const apiTotal = Number(apiStats?.total);
                const statsMatchFilter =
                    apiStats &&
                    Number.isFinite(apiTotal) &&
                    Number.isFinite(filteredTotal) &&
                    apiTotal === filteredTotal;

                if (statsMatchFilter) {
                    applyStatistics(apiStats);
                } else {
                    const countedRows = await loadRowsForStatistics(
                        sourceFilters,
                        pageRows,
                        pageInfo,
                        generation
                    );
                    if (generation !== loadGeneration.current) return;
                    applyStatistics(countQueueStatistics(countedRows));
                }
                notify("Messages fetched successfully!", "success");
            } else {
                setData([]);
                notify("Failed to fetch messages", "error");
            }
        } catch (error) {
            if (generation !== loadGeneration.current) return;
            console.error("Error fetching messages:", error);
            notify(
                error.response?.data?.error || "Failed to fetch messages",
                "error"
            );
            setData([]);
        } finally {
            if (generation === loadGeneration.current) {
                setLoading(false);
            }
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSearch = () => {
        fetchMessages(1, filters);
    };

    const handleReset = () => {
        const resetFilters = {
            status: "",
            recipient: "",
            campaign_name: "",
            template_name: "",
            template_id: "",
            date_from: null,
            date_to: null,
            date_field: "created_at",
            message_priority: "",
            language: "",
            per_page: 50,
            page: 1,
            sort_by: "created_at",
            sort_order: "desc",
        };
        setFilters(resetFilters);
        fetchMessages(1, resetFilters);
    };

    const handlePageChange = (event, value) => {
        fetchMessages(value);
    };

    const handleViewDetails = async (messageId) => {
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/message-queue/${messageId}`
            );

            if (response.data?.success) {
                setSelectedMessage(response.data.data);
                setDetailDialogOpen(true);
            } else {
                notify("Failed to fetch message details", "error");
            }
        } catch (error) {
            console.error("Error fetching message details:", error);
            notify(
                error.response?.data?.error || "Failed to fetch message details",
                "error"
            );
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
            return format(new Date(dateString), "yyyy-MM-dd HH:mm:ss");
        } catch (error) {
            return dateString;
        }
    };

    // Derived counters (requested logic)
    const totalCount = Number(statistics.total) || 0;
    const pendingCount = Number(statistics.pending) || 0;
    const sentAdjustedCount =
        (Number(statistics.sent) || 0) +
        (Number(statistics.delivered) || 0) +
        (Number(statistics.read) || 0);
    const deliveredAdjustedCount =
        (Number(statistics.delivered) || 0) + (Number(statistics.read) || 0);
    const readCount = Number(statistics.read) || 0;
    const failedCount = Number(statistics.failed) || 0;

    const percent2 = (value, denom) => {
        if (!denom || denom <= 0) return null;
        return (Math.round((value / denom) * 10000) / 100).toFixed(2);
    };

    const creditsInfoLikeCardSx = {
        height: "100%",
        background: "rgba(255, 255, 255, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.8)",
        borderRadius: 2,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
        transition: "all 0.3s ease",
        "&:hover": {
            background: "rgba(255, 255, 255, 0.75)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.1)",
        },
    };

    const statsCards = [
        {
            key: "total",
            label: "Total",
            value: totalCount,
            color: "text.primary",
            accent: "#334155",
            icon: <Inventory2Outlined fontSize="small" />,
            percent: null,
        },
        {
            key: "pending",
            label: "Pending",
            value: pendingCount,
            color: "warning.main",
            accent: "#d97706",
            icon: <ScheduleOutlined fontSize="small" />,
            percent: percent2(pendingCount, totalCount),
        },
        {
            key: "sent",
            label: "Sent",
            value: sentAdjustedCount,
            color: "info.main",
            accent: "#0284c7",
            icon: <SendOutlined fontSize="small" />,
            percent: percent2(sentAdjustedCount, totalCount),
        },
        {
            key: "delivered",
            label: "Delivered",
            value: deliveredAdjustedCount,
            color: "success.main",
            accent: "#16a34a",
            icon: <LocalShippingOutlined fontSize="small" />,
            percent: percent2(deliveredAdjustedCount, totalCount),
        },
        {
            key: "read",
            label: "Read",
            value: readCount,
            color: "primary.main",
            accent: "#2563eb",
            icon: <MarkEmailReadOutlined fontSize="small" />,
            percent: percent2(readCount, totalCount),
        },
        {
            key: "failed",
            label: "Failed",
            value: failedCount,
            color: "error.main",
            accent: "#dc2626",
            icon: <ReportProblemOutlined fontSize="small" />,
            percent: percent2(failedCount, totalCount),
        },
    ];

    return (
        <Box sx={{ ...WB_SX.page, p: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 2,
                }}
            >
                <Typography variant="h6" sx={{ ...WB_SX.pageTitle, mb: 0 }}>
                    WhatsApp Business Message Queue
                </Typography>
                <Tooltip title={showAdvancedFilters ? "Hide filters" : "Advanced filters"}>
                    <IconButton
                        onClick={() => setShowAdvancedFilters((prev) => !prev)}
                        aria-label="Advanced filters"
                        color={showAdvancedFilters ? "primary" : "default"}
                    >
                        <FilterList />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Filters */}
            {showAdvancedFilters && (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <TextField
                            fullWidth
                            label="Recipient Phone"
                            size="small"
                            value={filters.recipient}
                            onChange={(e) =>
                                handleFilterChange("recipient", e.target.value)
                            }
                            placeholder="e.g., +1234567890"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <TextField
                            fullWidth
                            label="Campaign Name"
                            size="small"
                            value={filters.campaign_name}
                            onChange={(e) =>
                                handleFilterChange("campaign_name", e.target.value)
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <TextField
                            fullWidth
                            label="Template Name"
                            size="small"
                            value={filters.template_name}
                            onChange={(e) =>
                                handleFilterChange("template_name", e.target.value)
                            }
                        />
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <TextField
                            fullWidth
                            label="Template ID"
                            size="small"
                            value={filters.template_id}
                            onChange={(e) =>
                                handleFilterChange("template_id", e.target.value)
                            }
                        />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) =>
                                    handleFilterChange("status", e.target.value)
                                }
                            >
                                <MenuItem value="">All</MenuItem>
                                {STATUS_OPTIONS.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {getStatusLabel(status)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Date Field</InputLabel>
                            <Select
                                value={filters.date_field}
                                label="Date Field"
                                onChange={(e) =>
                                    handleFilterChange("date_field", e.target.value)
                                }
                            >
                                {DATE_FIELD_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Date From"
                                value={filters.date_from}
                                onChange={(date) =>
                                    handleFilterChange("date_from", date)
                                }
                                renderInput={(params) => (
                                    <TextField {...params} size="small" fullWidth />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Date To"
                                value={filters.date_to}
                                onChange={(date) =>
                                    handleFilterChange("date_to", date)
                                }
                                renderInput={(params) => (
                                    <TextField {...params} size="small" fullWidth />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <TextField
                            fullWidth
                            label="Language"
                            size="small"
                            value={filters.language}
                            onChange={(e) =>
                                handleFilterChange("language", e.target.value)
                            }
                            placeholder="e.g., en_US"
                        />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Per Page</InputLabel>
                            <Select
                                value={filters.per_page}
                                label="Per Page"
                                onChange={(e) =>
                                    handleFilterChange("per_page", e.target.value)
                                }
                            >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={filters.sort_by}
                                label="Sort By"
                                onChange={(e) =>
                                    handleFilterChange("sort_by", e.target.value)
                                }
                            >
                                <MenuItem value="created_at">Created At</MenuItem>
                                <MenuItem value="schedule_date_time">Scheduled At</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort Order</InputLabel>
                            <Select
                                value={filters.sort_order}
                                label="Sort Order"
                                onChange={(e) =>
                                    handleFilterChange("sort_order", e.target.value)
                                }
                            >
                                <MenuItem value="asc">Ascending</MenuItem>
                                <MenuItem value="desc">Descending</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                        <Button variant="contained" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outlined" onClick={handleReset}>
                            Reset
                        </Button>
                        <Tooltip title="Refresh">
                            <Button
                                variant="contained"
                                onClick={() => fetchMessages(pagination.current_page)}
                                startIcon={<RefreshSharp />}
                            >
                                Refresh
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
            )}



            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {statsCards.map((item) => (
                    <Grid item xs={12} sm={6} md={2} key={item.key}>
                        <Card
                            sx={{
                                ...creditsInfoLikeCardSx,
                                position: "relative",
                                overflow: "hidden",
                            }}
                        >
                            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: 1.4,
                                            color: "text.secondary",
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            color: item.accent,
                                            backgroundColor: `${item.accent}20`,
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                </Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 800,
                                        color: "text.primary",
                                        letterSpacing: "-0.02em",
                                        mt: 0.25,
                                    }}
                                >
                                    {item.value.toLocaleString()}
                                </Typography>
                                {item.percent != null && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: item.accent,
                                            mt: 0.5,
                                        }}
                                    >
                                        {item.percent}% of total
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <FailedMessageSummaryCard
                failedSummary={statistics.failed_summary || []}
                failedTotal={failedCount}
            />

            {/* Table */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Total Rows: {pagination.total} | Page {pagination.current_page} of{" "}
                    {pagination.last_page}
                </Typography>
            </Box>

            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer sx={{ maxHeight: "calc(100vh - 100px)" }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    ID
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    Recipient
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    Template Name
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    Campaign
                                </TableCell>
                                {/* <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333",
                                        color: "#fff",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Language
                                </TableCell> */}
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                    align="center"
                                >
                                    Retry Count
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    Scheduled Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                >
                                    Sent At
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                    align="center"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                    align="center"
                                >
                                    Error Message
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...WB_SX.tableHeadCell,
                                    }}
                                    align="center"
                                >
                                    View Details
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((row) => {
                                    const isError131026 = hasErrorCode131026(row);
                                    const errorMessage = getErrorMessage(row);
                                    
                                    return (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            sx={{
                                                ...WB_SX.tableRow,
                                                backgroundColor: isError131026
                                                    ? "rgba(0, 0, 0, 0.13)"
                                                    : "inherit",
                                                opacity: isError131026 ? 0.7 : 1,
                                                "&:hover": {
                                                    backgroundColor: isError131026
                                                        ? "rgba(0, 0, 0, 0.06)"
                                                        : undefined,
                                                },
                                            }}
                                        >
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>{row.recipient || "-"}</TableCell>
                                            <TableCell>{row.template_name || "-"}</TableCell>
                                            <TableCell>{row.campaign_name || "-"}</TableCell>
                                            {/* <TableCell>{row.language || "-"}</TableCell> */}
                                            <TableCell align="center">
                                               {row.retry_allowed? `${row.retry_count ?? 0} out of 3 retries left` : "Not eligible for auto retry"}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(row.schedule_date_time)}
                                            </TableCell>
                                            <TableCell>{formatDate(row.sent_at)}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={getStatusLabel(row.status)}
                                                    color={getStatusColor(row.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {errorMessage && (row.status === 'FAILED' || row.status === 'AUTO_RETRYING') ? (
                                                    <Tooltip 
                                                        title={isError131026 ? getErrorTooltipText() : errorMessage}
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                            <ErrorOutline 
                                                                sx={{ 
                                                                    fontSize: 18, 
                                                                    color: isError131026 ? 'error.main' : 'warning.main' 
                                                                }} 
                                                            />
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: isError131026 ? 'error.main' : 'text.secondary',
                                                                    maxWidth: '200px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {errorMessage}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(row.id)}
                                                        color="primary"
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            {pagination.last_page > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                    }}
                >
                    <Pagination
                        count={pagination.last_page}
                        page={pagination.current_page}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Message Details Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Message Details
                    <IconButton
                        aria-label="close"
                        onClick={() => setDetailDialogOpen(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <Typography variant="h6">×</Typography>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedMessage && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Message ID
                                </Typography>
                                <Typography variant="body1">{selectedMessage.id}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Status
                                </Typography>
                                <Chip
                                    label={getStatusLabel(selectedMessage.status)}
                                    color={getStatusColor(selectedMessage.status)}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Recipient
                                </Typography>
                                <Typography variant="body1">
                                    {selectedMessage.recipient || "-"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Template Name
                                </Typography>
                                <Typography variant="body1">
                                    {selectedMessage.template_name || "-"}
                                </Typography>
                            </Box>
                            {/* <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Template ID
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                    {selectedMessage.template_id || "-"}
                                </Typography>
                            </Box> */}
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Campaign Name
                                </Typography>
                                <Typography variant="body1">
                                    {selectedMessage.campaign_name || "-"}
                                </Typography>
                            </Box>
                            {/* <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Language
                                </Typography>
                                <Typography variant="body1">
                                    {selectedMessage.language || "-"}
                                </Typography>
                            </Box> */}
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Retry Count
                                </Typography>
                                <Typography variant="body1">
                                    {(selectedMessage.retry_count ?? 0)}/3
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Message Priority
                                </Typography>
                                <Typography variant="body1">
                                    {selectedMessage.message_priority || "-"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Scheduled Date Time
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedMessage.schedule_date_time)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Sent At
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedMessage.sent_at)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Created At
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedMessage.created_at)}
                                </Typography>
                            </Box>
                            {selectedMessage.message_id && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        WhatsApp Message ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                        {selectedMessage.message_id}
                                    </Typography>
                                </Box>
                            )}
                            {(selectedMessage.error || selectedMessage.error_code || selectedMessage.error_message) && (
                                <Box>
                                    <Typography variant="subtitle2" color="error">
                                        Error
                                    </Typography>
                                    {selectedMessage.error_code && (
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Error Code:</strong> {selectedMessage.error_code}
                                        </Typography>
                                    )}
                                    {selectedMessage.error_message && (
                                        <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                                            {selectedMessage.error_message}
                                        </Typography>
                                    )}
                                    {selectedMessage.error && (
                                        <Typography variant="body2" color="error">
                                            {typeof selectedMessage.error === 'string' 
                                                ? selectedMessage.error 
                                                : JSON.stringify(selectedMessage.error, null, 2)}
                                        </Typography>
                                    )}
                                    {hasErrorCode131026(selectedMessage) && (
                                        <Box sx={{ mt: 1, p: 1, backgroundColor: 'error.light', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                Error 131026 - Message Undeliverable
                                            </Typography>
                                            <Typography variant="body2" component="div">
                                                Possible reasons:
                                            </Typography>
                                            <Typography variant="body2" component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                                                <li>Invalid Number: The phone number is not registered with WhatsApp.</li>
                                                <li>Outdated App: Recipient is using an old version of WhatsApp.</li>
                                                <li>Terms of Service: Recipient has not accepted the latest Meta privacy policy.</li>
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {selectedMessage.payload && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Payload
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{ p: 2, mt: 1, backgroundColor: "#f5f5f5" }}
                                    >
                                        <Typography
                                            variant="body2"
                                            component="pre"
                                            sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.75rem",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {JSON.stringify(selectedMessage.payload, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MessageQueue;

